import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/api";
import { useNavigate } from "react-router-dom";
import routes from "../../routes";
import { FaEye, FaEyeSlash } from "react-icons/fa"; // ✅ Import the eye icons
import { useTranslation } from "react-i18next";

const ChangePassword = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  }); // ✅ New state for password visibility

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ✅ New handler to toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPassword((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    if (formData.newPassword !== formData.confirmNewPassword) {
      setMessage(t("auth.changePassword.passwordsDoNotMatch"));
      setLoading(false);
      return;
    }

    if (formData.newPassword.length < 8) {
      setMessage(t("auth.common.passwordMinLength"));
      setLoading(false);
      return;
    }

    try {
      const response = await api.put(`/user/change-password`, formData);
      setMessage(response.data.message);

      // Redirect to login page to re-authenticate after a short delay
      setTimeout(() => {
        navigate(routes.login);
      }, 2000);
    } catch (error) {
      console.error("Error changing password:", error);
      setMessage(
        error.response?.data?.message || t("auth.changePassword.error"),
      );
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="text-center mt-10 p-6 rounded-lg shadow-md bg-red-100 text-red-700">
        {t("auth.changePassword.notLoggedIn")}
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      <h1 className="text-4xl font-bold text-gray-800 mb-8 text-center">
        {t("auth.changePassword.title")}
      </h1>
      <div className="bg-white p-6 rounded-lg shadow-md mb-8 border border-gray-200">
        <form onSubmit={handleSubmit}>
          {message && (
            <p
              className={`mb-4 text-center ${
                message.includes("success") ? "text-green-500" : "text-red-500"
              }`}
            >
              {message}
            </p>
          )}

          <div className="mb-4">
            <label className="block text-gray-700">
              {t("auth.changePassword.currentPassword")}
            </label>
            <div className="relative">
              <input
                type={showPassword.current ? "text" : "password"} // ✅ Dynamic type
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <span
                onClick={() => togglePasswordVisibility("current")} // ✅ Toggle on click
                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500 hover:text-gray-700"
              >
                {showPassword.current ? <FaEyeSlash /> : <FaEye />}{" "}
                {/* ✅ Render icon based on state */}
              </span>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-gray-700">
              {t("auth.changePassword.newPassword")}
            </label>
            <div className="relative">
              <input
                type={showPassword.new ? "text" : "password"}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <span
                onClick={() => togglePasswordVisibility("new")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500 hover:text-gray-700"
              >
                {showPassword.new ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-gray-700">
              {t("auth.changePassword.confirmPassword")}
            </label>
            <div className="relative">
              <input
                type={showPassword.confirm ? "text" : "password"}
                name="confirmNewPassword"
                value={formData.confirmNewPassword}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <span
                onClick={() => togglePasswordVisibility("confirm")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500 hover:text-gray-700"
              >
                {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>
          <button
            type="submit"
            className="w-full bg-blue-500 text-white font-bold py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none"
            disabled={loading}
          >
            {loading
              ? t("auth.changePassword.changing")
              : t("auth.changePassword.submit")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
